import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, OperatorFunction, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { SeoApiErrorService } from '../dashboard/services/seo-api-error.service';
import { AuditLogLimit, AuditLogsResponse } from './audit-logs/audit-log.model';

export type AdminMemberRole = 'Admin' | 'User';

export interface AdminConsoleMember {
  id: string;
  name: string;
  email: string;
  role: AdminMemberRole;
}

export interface AdminMembersSummary {
  total: number;
  admins: number;
  users: number;
}

export interface AdminMembersResponse {
  members: AdminConsoleMember[];
  summary: AdminMembersSummary;
}

export interface AdminUserOperationFailure {
  email: string;
  error: string;
}

export interface AdminUserOperationResult {
  succeeded: string[];
  failed: AdminUserOperationFailure[];
}

export interface AdminStatusResponse {
  isAdmin: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AdminApiService {
  private http = inject(HttpClient);
  private apiErrors = inject(SeoApiErrorService);
  private readonly baseUrl = environment.apiUrl;
  private readonly jsonHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  getCurrentUserStatus(): Observable<AdminStatusResponse> {
    return this.http.get<AdminStatusResponse>(`${this.baseUrl}/admin/me`);
  }

  getMembers(): Observable<AdminMembersResponse> {
    return this.http.get<AdminMembersResponse>(`${this.baseUrl}/admin/members`).pipe(
      this.handleApiError('Failed to load members.')
    );
  }

  addUsers(emails: string[]): Observable<AdminUserOperationResult> {
    const normalizedEmails = this.normalizeEmails(emails);
    if (normalizedEmails.length === 0) {
      return of({ succeeded: [], failed: [] });
    }
    return this.http.post<AdminUserOperationResult>(
      `${this.baseUrl}/admin/users`,
      { emails: normalizedEmails },
      { headers: this.jsonHeaders }
    ).pipe(
      this.handleApiError('Failed to add users.')
    );
  }

  removeUsers(emails: string[]): Observable<AdminUserOperationResult> {
    const normalizedEmails = this.normalizeEmails(emails);
    if (normalizedEmails.length === 0) {
      return of({ succeeded: [], failed: [] });
    }
    return this.http.delete<AdminUserOperationResult>(
      `${this.baseUrl}/admin/users`,
      { headers: this.jsonHeaders, body: { emails: normalizedEmails } }
    ).pipe(
      this.handleApiError('Failed to remove users.')
    );
  }

  getAuditLogs(limit: AuditLogLimit = 100): Observable<AuditLogsResponse> {
    return this.http.get<AuditLogsResponse>(
      `${this.baseUrl}/admin/audit-logs`,
      { params: { limit: limit.toString() } }
    ).pipe(
      this.handleApiError('Failed to load audit logs.')
    );
  }

  private handleApiError<T>(fallbackMessage: string): OperatorFunction<T, T> {
    return catchError((err) => this.apiErrors.handle(err, fallbackMessage));
  }

  private normalizeEmails(emails: string[]): string[] {
    return Array.from(new Set(
      emails
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    ));
  }
}
